/**
 * Who is using the application, and which shell that opens.
 *
 * `/api/auth/me` is the only place an identity comes from — for a child it is
 * also the only place their own `child_id` exists, and it is never put in a URL
 * afterwards. The shell is set here so `api/client.ts` can refuse, at run time,
 * any call a child must not be able to make.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { call, setShell } from "@/api/client";
import { oublierTout } from "@/ui/enCache";
import { ApiError } from "@/api/errors";
import type { Me } from "@/api/schema";
import { profileForLevel, type Profile } from "@/config/profile";

type Status = "loading" | "anonymous" | "signed-in";

type SessionValue = {
  status: Status;
  me: Me | null;
  profile: Profile;
  isChild: boolean;
  isParent: boolean;
  refresh: () => Promise<void>;
  signInParent: (email: string, password: string) => Promise<void>;
  signInChild: (pin: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [me, setMe] = useState<Me | null>(null);

  const adopt = useCallback((identity: Me | null) => {
    setMe(identity);
    setStatus(identity ? "signed-in" : "anonymous");
    setShell(identity ? (identity.role === "child" ? "child" : "parent") : "unknown");
  }, []);

  const refresh = useCallback(async () => {
    try {
      adopt(await call("me"));
    } catch (error) {
      if (error instanceof ApiError && error.needsLogin) adopt(null);
      else adopt(null);
    }
  }, [adopt]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      me,
      profile: profileForLevel(me?.level ?? null),
      isChild: me?.role === "child",
      isParent: me?.role === "parent",
      refresh,
      signInParent: async (email, password) => {
        await call("parentLogin", { body: { email, password } });
        await refresh();
      },
      signInChild: async (pin) => {
        await call("childLogin", { body: { pin } });
        await refresh();
      },
      signOut: async () => {
        try {
          await call("logout");
        } finally {
          adopt(null);
          // Le contenu des cahiers gardé dans le navigateur part avec la
          // session : la règle 8 le tient derrière une authentification, et
          // une copie qui survit à la déconnexion n'y est plus.
          oublierTout();
        }
      },
    }),
    [status, me, refresh, adopt],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession hors SessionProvider");
  return value;
}
